import { Point, neighbors } from '../math/point'
import { vector, Vector, add } from '../math/vector'
import { toQuadrant, QUADRANT_ANGLES } from '../math/quadrant'
import { normalizeAngle } from '../math/angle'
import { Entity } from './entity';
import { Weapon } from './weapon'

import { Sprite } from './sprite'
import { VehicleModel } from './vehicle'
import clone from 'immutability-helper'
import { WorldGrid } from './world-grid';
import { range } from '../math/util';


export class ShipClass {
  constructor(
    readonly className: string,
    readonly speed: number,      // how much distance forward during move action
    readonly turnSpeed: number,  // how much distance sideways during move action
    readonly hullPoints: number,
    readonly shields: [number, number, number, number],
    readonly weapons: Weapon[]) {}

  create(name: string, position: Point, heading: number, sprite: Sprite): Ship {
    return new Ship(name, position, heading, sprite,
      this.className, this.speed, this.turnSpeed, this.hullPoints,
      this.shields, this.weapons)
  }
}

export class Ship implements Entity {
  // readonly vehicleModel: VehicleModel

  readonly maxSpeed: number
  readonly maxTurnSpeed: number
  readonly maxHullPoints: number
  readonly maxShields: readonly [number, number, number, number]

  readonly targetLock?: Ship
  readonly destination?: Point

  constructor(
    readonly name: string,
    readonly position: Point,
    readonly heading: number,
    readonly sprite: Sprite,
    readonly className: string,
    readonly speed: number,      // how much distance forward during move action
    readonly turnSpeed: number,  // how much distance sideways during move action
    readonly hullPoints: number,
    readonly shields: readonly [number, number, number, number],
    readonly weapons: Weapon[]
  ) {
    this.maxHullPoints = hullPoints
    // this.shields = shields
    this.maxShields = shields

    this.maxSpeed = speed
    this.maxTurnSpeed = turnSpeed
    // this.vehicleModel = new VehicleModel(speed, turnSpeed)
  }

  vehicleModel() {
    return new VehicleModel(this.speed, this.turnSpeed)
  }

  maxVehicleModel() {
    return new VehicleModel(this.maxSpeed, this.maxTurnSpeed)
  }

  update(elapsed: number): this {
    return this as this // clone(this as Ship, {})
  }

  // add velocity to vector
  // p in world coordinates
  move(velocity: Vector): Ship {
    const position = add(this.position, velocity)
    const heading = velocity.angle()

    return clone(this as Ship, {
      position: {$set: position},
      heading: {$set: heading}
    })
  }

  // returns the quandrant index of this ship as seen from the source point
  quadrant(target: Point): number {
    return toQuadrant(this.position, this.heading, target)
  }

  // returns the current angle of the quandrant
  quadrantAngle(quadrant: number): number {
    return normalizeAngle(this.heading + QUADRANT_ANGLES[quadrant])
  }

  private hullDamage(damage: number): Ship {
    return clone(this as Ship, {hullPoints: {$set: this.hullPoints-damage}})
  }

  private setShield(quadrant: number, newShield: number): Ship {
    const newShields = clone(this.shields, {[quadrant]: {$set: newShield}})
    return clone(this as Ship, {shields: {$set: newShields}})
  }

  damage(source: Point, damage: number, bypassDamage: number): Ship {
    const newShip = this.hullDamage(bypassDamage)
    //
    const shieldQuadrant = this.quadrant(source)
    const shield = this.shields[shieldQuadrant]
    const newShield = shield - damage
    if (newShield < 0 ) {
      const newShip1 = newShip.setShield(shieldQuadrant, 0)
      return newShip1.hullDamage(-newShield)
    } else {
      return newShip.setShield(shieldQuadrant, newShield)
    }
  }

  maxVelocity(v0: Vector): Vector {
    return this.vehicleModel().maxVelocity(this, v0)
  }

  canMove(p: Point): boolean {
    return this.vehicleModel().canMove(this, p)
  }

  setWeapon(newWeapon: Weapon): Ship {
    const index = this.weapons.findIndex(s => {
      return (s.id === newWeapon.id)
    })

    const newWeapons = clone(this.weapons, {[index]: {$set: newWeapon}})
    return clone(this as Ship, {weapons: {$set: newWeapons}})
  }

  setShields(newShields: [number,number,number,number]): Ship {
    return clone(this as Ship, {shields: {$set: newShields}})
  }

  setWeapons(newWeapons: Weapon[]): Ship {
    return clone(this as Ship, {weapons: {$set: newWeapons}})
  }

  setDestination(p: Point): Ship {
    return clone(this as Ship, {destination: {$set: p}})
  }

  endPhase(): Ship {
    const newWeapons = this.weapons.map(w => w.endPhase())
    const ship0 = this.setWeapons(newWeapons)

    //
    const s = ship0.shields.map((s, i) => {
      if (s > this.maxShields[i]) {
        return this.maxShields[i]
      }
      return s
    })
    const ship1 = ship0.setShields([s[0], s[1], s[2], s[3]])

    const ship2 = clone(ship1, {speed: {$set: this.maxSpeed}, turnSpeed: {$set: this.maxTurnSpeed}})

    return ship2
  }
}
